import { useLayoutEffect, useRef, useState, type RefObject } from "react";
import type { Analytics } from "./analytics";

/** Where the full account of what is measured lives. */
const DETAILS_URL = "https://tryantar.com/privacy/#web-analytics";
/** Tailwind's `md`: from here up the bar floats in a corner and covers nothing. */
const DESKTOP_QUERY = "(min-width: 768px)";
const INSET_PROPERTY = "--consent-inset";

/**
 * One short line at the foot of the screen, asked once. The choice is kept
 * and applies on every later visit.
 *
 * On a phone the bar takes its own room rather than covering the page: its
 * height is published as `--consent-inset`, which every screen subtracts, so
 * a first visitor always sees "Get started" above it.
 */
export function ConsentBanner({ analytics }: { readonly analytics: Analytics }) {
  const [open, setOpen] = useState(() => analytics.enabled && analytics.consent() === null);
  const bar = useRef<HTMLElement>(null);
  useReserveRoom(bar, open);
  if (!open) return null;

  function choose(granted: boolean) {
    analytics.setConsent(granted);
    setOpen(false);
  }

  return (
    <section
      ref={bar}
      aria-label="Analytics"
      className="fixed inset-x-0 bottom-0 z-50 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-rule bg-paper px-gutter pb-[calc(0.5rem+env(safe-area-inset-bottom))] pt-2 text-ink md:inset-x-auto md:bottom-4 md:right-4 md:max-w-[380px] md:border md:pb-3 md:pt-3 md:shadow-[0_-4px_16px_rgba(0,0,0,0.06)]"
    >
      <p className="w-full text-[14px] leading-[1.4]">
        We measure how Antar is used, with Google Analytics and PostHog. Your birth details and what you tell Antar stay
        hidden.{" "}
        <a href={DETAILS_URL} target="_blank" rel="noreferrer" className="underline underline-offset-4">
          Details
        </a>
      </p>
      <button type="button" onClick={() => choose(true)} className="label min-h-11 text-ink underline underline-offset-4">
        Allow
      </button>
      <button type="button" onClick={() => choose(false)} className="label min-h-11 text-mid underline underline-offset-4">
        No thanks
      </button>
    </section>
  );
}

/** Publishes the bar's height while it is open on a phone; clears it otherwise. */
function useReserveRoom(bar: RefObject<HTMLElement | null>, open: boolean): void {
  useLayoutEffect(() => {
    const root = document.documentElement;
    const element = bar.current;
    if (!open || !element) {
      root.style.removeProperty(INSET_PROPERTY);
      return;
    }
    const desktop = window.matchMedia?.(DESKTOP_QUERY);
    const update = () => {
      const covers = !desktop?.matches;
      root.style.setProperty(INSET_PROPERTY, covers ? `${Math.ceil(element.getBoundingClientRect().height)}px` : "0px");
    };
    update();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(element);
    desktop?.addEventListener?.("change", update);
    return () => {
      observer?.disconnect();
      desktop?.removeEventListener?.("change", update);
      root.style.removeProperty(INSET_PROPERTY);
    };
  }, [bar, open]);
}
